import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const QuickViewModal = ({ product, isOpen, onClose, onEnquire }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedCapacity, setSelectedCapacity] = useState(product?.capacityOptions?.[0]);

  if (!product) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(price);
  };

  const nextImage = () => {
    if (product?.images && product?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % product?.images?.length);
    }
  };

  const prevImage = () => {
    if (product?.images && product?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + product?.images?.length) % product?.images?.length);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative bg-card rounded-2xl liquid-shadow max-w-4xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white"
            >
              <Icon name="X" size={20} />
            </Button>

            <div className="grid grid-cols-1 lg:grid-cols-2 h-full max-h-[90vh]">
              {/* Image Section */}
              <div className="relative bg-gradient-to-br from-muted to-background">
                <div className="aspect-square relative overflow-hidden">
                  <Image
                    src={product?.images ? product?.images?.[currentImageIndex] : product?.image}
                    alt={product?.imageAlt}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Image Navigation */}
                  {product?.images && product?.images?.length > 1 && (
                    <>
                      <Button
                        variant="secondary"
                        size="icon"
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white"
                      >
                        <Icon name="ChevronLeft" size={20} />
                      </Button>
                      <Button
                        variant="secondary"
                        size="icon"
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white"
                      >
                        <Icon name="ChevronRight" size={20} />
                      </Button>
                    </>
                  )}
                </div>

                {/* Image Thumbnails */}
                {product?.images && product?.images?.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                    {product?.images?.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-3 h-3 rounded-full brand-transition ${
                          index === currentImageIndex ? 'bg-primary' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Content Section */}
              <div className="p-6 overflow-y-auto">
                {/* Category & Badges */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-inter font-medium text-primary uppercase tracking-wide">
                    {product?.category}
                  </span>
                  <div className="flex space-x-2">
                    {product?.isNew && (
                      <span className="px-2 py-1 bg-accent text-white text-xs font-inter font-medium rounded-full">
                        New
                      </span>
                    )}
                    {product?.sustainabilityScore && (
                      <div className="flex items-center space-x-1 px-2 py-1 bg-trust text-white rounded-full">
                        <Icon name="Leaf" size={12} />
                        <span className="text-xs font-inter font-medium">{product?.sustainabilityScore}%</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Product Name */}
                <h2 className="text-2xl font-poppins font-bold text-text-primary mb-2">
                  {product?.name}
                </h2>

                {/* Rating */}
                {product?.rating && (
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="flex items-center space-x-1">
                      {[...Array(5)]?.map((_, i) => (
                        <Icon
                          key={i}
                          name="Star"
                          size={16}
                          className={i < Math.floor(product?.rating) ? "text-warning fill-warning" : "text-gray-300"}
                        />
                      ))}
                    </div>
                    <span className="text-sm font-inter text-text-secondary">
                      {product?.rating} ({product?.reviewCount} reviews)
                    </span>
                  </div>
                )}

                {/* Price */}
                <div className="flex items-center space-x-3 mb-6">
                  <span className="text-3xl font-poppins font-bold text-text-primary">
                    {formatPrice(product?.price)}
                  </span>
                  {product?.originalPrice && product?.originalPrice > product?.price && (
                    <>
                      <span className="text-lg font-inter text-text-secondary line-through">
                        {formatPrice(product?.originalPrice)}
                      </span>
                      <span className="px-3 py-1 bg-success/10 text-success text-sm font-inter font-medium rounded-full">
                        {Math.round(((product?.originalPrice - product?.price) / product?.originalPrice) * 100)}% OFF
                      </span>
                    </>
                  )}
                </div>

                {/* Description */}
                <p className="text-text-secondary font-inter mb-6 leading-relaxed">
                  {product?.description}
                </p>

                {/* Capacity Options */}
                {product?.capacityOptions && (
                  <div className="mb-6">
                    <h4 className="text-sm font-poppins font-semibold text-text-primary mb-3">
                      Choose Capacity
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {product?.capacityOptions?.map((capacity) => (
                        <button
                          key={capacity}
                          onClick={() => setSelectedCapacity(capacity)}
                          className={`px-4 py-2 rounded-lg border brand-transition ${
                            selectedCapacity === capacity
                              ? 'border-primary bg-primary text-white' :'border-border text-text-secondary hover:border-primary'
                          }`}
                        >
                          {capacity}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Key Features */}
                <div className="mb-6">
                  <h4 className="text-sm font-poppins font-semibold text-text-primary mb-3">
                    Key Features
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center space-x-2">
                      <Icon name="Droplets" size={16} className="text-accent" />
                      <span className="text-sm font-inter text-text-secondary">{product?.capacity}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Thermometer" size={16} className="text-accent" />
                      <span className="text-sm font-inter text-text-secondary">{product?.insulation}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Shield" size={16} className="text-accent" />
                      <span className="text-sm font-inter text-text-secondary">{product?.material}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Award" size={16} className="text-accent" />
                      <span className="text-sm font-inter text-text-secondary">{product?.warranty}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    size="lg"
                    className="flex-1"
                  >
                    <Icon name="Heart" size={18} className="mr-2" />
                    Add to Wishlist
                  </Button>
                  <Button
                    variant="default"
                    size="lg"
                    onClick={() => onEnquire(product)}
                    className="flex-1 bg-primary hover:bg-primary/90"
                  >
                    <Icon name="MessageCircle" size={18} className="mr-2" />
                    Enquire Now
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default QuickViewModal;